import { decimal, minDecimal, money, nonNegative } from "./money";
import type { LegalParameters, TaxBracket } from "./parameters";

function applyTable(taxableIncome: number, table: TaxBracket[]): number {
  const income = nonNegative(taxableIncome);
  if (income.isZero()) return 0;
  const bracket =
    table.find(
      ({ lower, upper }) =>
        income.greaterThanOrEqualTo(lower) && income.lessThanOrEqualTo(upper),
    ) ?? table[table.length - 1];
  return money(
    decimal(bracket.fixed).plus(
      income.minus(bracket.lower).times(bracket.rate),
    ),
  );
}

function scaledDailyTable(
  table: TaxBracket[],
  days: number,
): TaxBracket[] {
  return table.map((bracket) => ({
    lower: money(decimal(bracket.lower).times(days)),
    upper: Number.isFinite(bracket.upper)
      ? money(decimal(bracket.upper).times(days))
      : Number.POSITIVE_INFINITY,
    fixed: money(decimal(bracket.fixed).times(days)),
    rate: bracket.rate,
  }));
}

export interface PeriodTaxResult {
  taxBeforeSubsidy: number;
  subsidyCalculated: number;
  subsidyApplied: number;
  isrWithheld: number;
  monthlyEquivalent: number;
}

export function calculatePeriodIncomeTax(
  taxableIncome: number,
  days: number,
  params: LegalParameters,
  options: { monthly?: boolean; subsidyEligible?: boolean } = {},
): PeriodTaxResult {
  const safeDays = Math.max(1, Math.min(30.4, days));
  const monthly = options.monthly === true;
  const table = monthly
    ? params.monthlyTaxTable
    : scaledDailyTable(params.dailyTaxTable, safeDays);
  const taxBeforeSubsidy = applyTable(taxableIncome, table);
  const monthlyEquivalent = monthly
    ? money(taxableIncome)
    : money(decimal(taxableIncome).times(30.4).div(safeDays));
  const eligible =
    options.subsidyEligible !== false &&
    monthlyEquivalent <= params.subsidyMonthlyIncomeLimit;
  const tabularSubsidy = params.subsidyTable?.find(
    ({ upper }) => monthlyEquivalent <= upper,
  )?.amount;
  const monthlySubsidy = decimal(
    tabularSubsidy ??
      decimal(params.umaMonthly).times(params.subsidyUmaRate),
  );
  const subsidyCalculated = eligible
    ? money(
        monthly
          ? monthlySubsidy
          : monthlySubsidy.div(30.4).times(safeDays),
      )
    : 0;
  const subsidyApplied = money(
    minDecimal(subsidyCalculated, taxBeforeSubsidy),
  );

  return {
    taxBeforeSubsidy,
    subsidyCalculated,
    subsidyApplied,
    isrWithheld: money(decimal(taxBeforeSubsidy).minus(subsidyApplied)),
    monthlyEquivalent,
  };
}

export function calculateSeparationWithholding(
  taxableSeparationPayment: number,
  lastOrdinaryMonthlySalary: number,
  params: LegalParameters,
): {
  withholding: number;
  effectiveRate: number;
  method: "monthly-table" | "last-salary-rate";
} {
  const taxable = nonNegative(taxableSeparationPayment);
  const monthlySalary = nonNegative(lastOrdinaryMonthlySalary);
  if (taxable.isZero() || monthlySalary.isZero()) {
    return { withholding: 0, effectiveRate: 0, method: "monthly-table" };
  }

  if (taxable.lessThan(monthlySalary)) {
    const withholding = calculatePeriodIncomeTax(
      taxable.toNumber(),
      30.4,
      params,
      { monthly: true, subsidyEligible: false },
    ).taxBeforeSubsidy;
    return {
      withholding,
      effectiveRate: taxable.isZero()
        ? 0
        : decimal(withholding).div(taxable).toNumber(),
      method: "monthly-table",
    };
  }

  const salaryTax = calculatePeriodIncomeTax(
    monthlySalary.toNumber(),
    30.4,
    params,
    { monthly: true, subsidyEligible: true },
  ).isrWithheld;
  const effectiveRate = decimal(salaryTax).div(monthlySalary).toNumber();
  return {
    withholding: money(taxable.times(effectiveRate)),
    effectiveRate,
    method: "last-salary-rate",
  };
}
